import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import TicketListItem from "../TicketListItemCustom";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  tabContainer: {
    overflowY: "auto",
    maxHeight: 350,
    ...theme.scrollbarStyles,
  },
  popoverPaper: {
    width: "100%",
    maxWidth: 350,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      maxWidth: 270,
    },
  },
  noShadow: {
    boxShadow: "none !important",
  },
  icons: {
    color: "#fff",
  },
  customBadge: {
    backgroundColor: "#34E23C",
    color: "#fff",
  },
}));

const NotificationsPopOver = ({ volume }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const ticketIdUrl = +history.location.pathname.split("/")[2];
  const ticketIdRef = useRef(ticketIdUrl);
  const anchorEl = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { profile, queues } = user;
  const [, setDesktopNotifications] = useState([]);
  const { tickets } = useTickets({ withUnreadMessages: "true" });
  const [play, { stop }] = useSound(alertSound, { volume });
  const soundAlertRef = useRef();
  const historyRef = useRef(history);
  const socketManager = useContext(SocketContext);
  const intervalRef = useRef(null);

  useEffect(() => {
    soundAlertRef.current = play;
    if ("Notification" in window) {
      Notification.requestPermission();
    }
  }, [play]);

  useEffect(() => {
    const queueIds = queues.map((q) => q.id);
    const filteredTickets = tickets.filter(
      (t) => queueIds.indexOf(t.queueId) > -1
    );

    if (profile === "user") {
      setNotifications(filteredTickets);
    } else {
      setNotifications(tickets);
    }
  }, [tickets, queues, profile]);

  useEffect(() => {
    ticketIdRef.current = ticketIdUrl;
  }, [ticketIdUrl]);

  useEffect(() => {
    const soundLoopDuration = (parseInt(process.env.REACT_APP_SOUND_LOOP, 10) || 10) * 1000;
    const enableSoundLoop = process.env.REACT_APP_ENABLE_SOUND_LOOP === 'true';

    if (notifications.length > 0 && enableSoundLoop) {
      clearInterval(intervalRef.current);
      play();
      intervalRef.current = setInterval(() => {
        play();
      }, soundLoopDuration);
    } else {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      stop();
    }

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [notifications, play, stop]);

  useEffect(() => {
    const enableSoundLoop = process.env.REACT_APP_ENABLE_SOUND_LOOP === 'true';
    if (!enableSoundLoop && notifications.length > 0) {
      play();
      setTimeout(stop, 5000);
    }
  }, [notifications, play, stop]);

  useEffect(() => {
    if (!user) return;
    const socket = socketManager.getSocket(user.companyId);

    const onCompanyTicket = (data) => {
      if (
        data.action === "updateUnread" ||
        data.action === "delete" ||
        data.action === "removeFromList"
      ) {

        setNotifications((prevState) => {
          const ticketIndex = prevState.findIndex((t) => t.id === data.ticketId);
          if (ticketIndex !== -1) {
            const updatedNotifications = [...prevState];
            updatedNotifications.splice(ticketIndex, 1);
            return updatedNotifications;
          }

          if (data?.ticket?.unreadMessages > 0) {
            return [data.ticket, ...prevState];
          } else {
            return [...prevState];
          }
        });

        setDesktopNotifications((prevState) => {
          const notificationIndex = prevState.findIndex(
            (n) => n.tag === String(data.ticketId)
          );
          if (notificationIndex !== -1) {
            prevState[notificationIndex].close();
            const updatedNotifications = [...prevState];
            updatedNotifications.splice(notificationIndex, 1);
            return updatedNotifications;
          }
          return prevState;
        });
      }
    };

    const onCompanyAppMessage = (data) => {
      const queueIds = queues.map((q) => q.id);
      if (
        data.action === "create" &&
        (queueIds.indexOf(data.ticket.queueId) > -1 || !data.ticket.queueId) &&
        (data.ticket.userId === user?.id || !data.ticket.userId) &&
        (data.ticket.unreadMessages > 0 || !data.ticket.unreadMessages) &&
        data.ticket?.status !== "closed"
      ) {
        setNotifications((prevState) => {
          const ticketIndex = prevState.findIndex((t) => t.id === data.ticket.id);

          if (ticketIndex > -1 && data.message.read) {
            const updatedNotifications = [...prevState];
            updatedNotifications.splice(ticketIndex, 1);
            return updatedNotifications;
          } else if (ticketIndex > -1) {
            const updatedNotifications = [...prevState];
            updatedNotifications[ticketIndex] = data.ticket;
            return updatedNotifications;
          } else {
            if (data?.ticket?.unreadMessages > 0) {
              return [data.ticket, ...prevState];
            } else {
              return [...prevState];
            }
          }
        });

        const shouldNotNotificate =
          (data.message.ticketId === ticketIdRef.current &&
            document.visibilityState === "visible") ||
          (data.ticket.userId && data.ticket.userId !== user?.id) ||
          data.ticket.isGroup ||
          data.ticket.isBot;

        if (shouldNotNotificate) return;
        handleNotifications(data);
      }
    };

    socket.on("connect", () => socket.emit("joinNotification"));
    socket.on(`company-${user.companyId}-ticket`, onCompanyTicket);
    socket.on(`company-${user.companyId}-appMessage`, onCompanyAppMessage);

    return () => {
      socket.off("connect", () => socket.emit("leaveNotification"));
      socket.off(`company-${user.companyId}-ticket`, onCompanyTicket);
      socket.off(`company-${user.companyId}-appMessage`, onCompanyAppMessage);
    };
  }, [user, profile, queues, socketManager]);

  const handleNotifications = (data) => {
    const { message, contact, ticket } = data;

    const options = {
      body: `${message.body} - ${format(new Date(), "HH:mm")}`,
      icon: contact.profilePicUrl,
      tag: ticket.id,
      renotify: true,
    };

    const notification = new Notification(
      `Nuevo mensaje de ${contact.name}`,
      options
    );

    notification.onclick = (e) => {
      e.preventDefault();
      window.focus();
      setNotifications((prevState) =>
        prevState.filter((notification) => notification.id !== ticket.id)
      );
      historyRef.current.push(`/tickets/${ticket.uuid}`);
    };

    setDesktopNotifications((prevState) => {
      const notificationIndex = prevState.findIndex(
        (n) => n.tag === notification.tag
      );
      if (notificationIndex !== -1) {
        prevState[notificationIndex] = notification;
        return [...prevState];
      }
      return [notification, ...prevState];
    });
  };

  const handleClick = () => {
    setIsOpen((prevState) => !prevState);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const NotificationTicket = ({ children }) => {
    return <div onClick={handleClickAway}>{children}</div>;
  };

  return (
    <>
      <IconButton
        className={classes.icons}
        onClick={handleClick}
        ref={anchorEl}
        aria-label="Open Notifications"
        variant="contained"
      >
        <Badge
          overlap="rectangular"
          badgeContent={notifications.length}
          classes={{ badge: classes.customBadge }}
        >
          <WhatsAppIcon />
        </Badge>
      </IconButton>
      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
        onClose={handleClickAway}
      >
        <List dense className={classes.tabContainer}>
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText>No hay notificaciones</ListItemText>
            </ListItem>
          ) : (
            notifications.map((ticket) => (
              <NotificationTicket key={ticket.id}>
                <TicketListItem ticket={ticket} />
              </NotificationTicket>
            ))
          )}
        </List>
      </Popover>
    </>
  );
};

export default NotificationsPopOver;
